import { useCallback, useMemo } from "react"

import axios from "axios"
import BigNumber from "bignumber.js"
import BN from "bn.js"
import dayjs from "dayjs"
import { useAtomValue } from "jotai"

import { connectionAtom, networkAtom } from "@/atoms/settings"
import { Constants } from "@/programs/voucher_artifacts/constants.nft"
import VoucherNftIDL from "@/programs/voucher_artifacts/nft_voucher.json"
import { PDA as NFTPDA } from "@/programs/voucher_artifacts/pda.nft"
import { NetworkType } from "@/programs/voucher_artifacts/types"
import { VoucherNft as RenecVoucherNftType } from "@/programs/voucher_artifacts/voucher_nft.renec"
import { SolanaVoucherNft as SolanaVoucherNftType } from "@/programs/voucher_artifacts/voucher_nft.solana"
import { IS_MAINNET, SOLANA_NFT_VOUCHER_PROGRAM_ID } from "@/utils/constants"
import { SendTransactionError } from "@/utils/dataTypes"
import { commonErrorResponse, sliceString, stringifyVariable, waitForTransactionConfirmation } from "@/utils/helpers"
import * as anchor from "@project-serum/anchor"
import NodeWallet from "@project-serum/anchor/dist/cjs/nodewallet"
import { BlockchainNetwork, getRelendProgramId, getSolanaProgramId } from "@renec-foundation/relend-adapter"
import { DemonWalletAdapter } from "@renec-foundation/wallet-adapter-react"
import { ASSOCIATED_TOKEN_PROGRAM_ID, TOKEN_PROGRAM_ID, Token } from "@solana/spl-token"
import {
  ComputeBudgetProgram,
  Keypair,
  PublicKey,
  Transaction,
  TransactionMessage,
  VersionedTransaction,
} from "@solana/web3.js"

import { NFTDetail, NFTDetailType } from "./useFetchNFT"
import useTrans from "./useTrans"
import useWalletConnect from "./useWalletConnect"

const DATETIME_FORMAT = "DD/MM/YYYY HH:mm:ss"
const NFT_SEED = "seed1"
const RANDOM_KEYPAIR = Keypair.generate()
const RANDOM_WALLET = new NodeWallet(RANDOM_KEYPAIR)

export type RestakingResult = {
  isSucceeded: boolean
  tx?: string
  error?: string
}

export function modifyComputeUnitIx(units?: number) {
  return ComputeBudgetProgram.setComputeUnitLimit({
    units: units || 1000000,
  })
}

const parseNFTDetail = async (url?: string) => {
  if (!url) return

  try {
    const { data } = await axios.get(url)
    const nftDetail = data as NFTDetail
    const attributes = nftDetail.attributes

    const currentDate = dayjs()
    const start_time = attributes.find((x) => x.trait_type === "start_time")
    if (start_time) {
      const parsedStartTime = dayjs(start_time.value, DATETIME_FORMAT, true)
      nftDetail.isValidStartDate = parsedStartTime.isBefore(currentDate)
    }

    const end_time = attributes.find((x) => x.trait_type === "end_time")
    if (end_time) {
      const parsedEndTime = dayjs(end_time.value, DATETIME_FORMAT, true)
      nftDetail.isValidEndDate = parsedEndTime.isAfter(currentDate)
    }

    // const eligible_tokens = attributes.find((x) => x.trait_type === "eligible_tokens")
    // if (eligible_tokens) {
    //   nftDetail.isValidToken =
    //     eligible_tokens.value === "" || eligible_tokens.value.split(",").includes(repayTokenAddress)
    //   nftDetail.eligibleTokens = eligible_tokens.value
    // }

    const discount_percentage = attributes.find((x) => x.trait_type === "discount_percentage")
    if (discount_percentage) {
      nftDetail.discountPercentage = new BigNumber(discount_percentage.value)
    }

    const maximum_amount = attributes.find((x) => x.trait_type === "maximum_amount")
    if (maximum_amount) {
      nftDetail.maximumAmount = new BigNumber(maximum_amount.value)
    }

    nftDetail.isValid = nftDetail.isValidStartDate && nftDetail.isValidEndDate && nftDetail.isValidToken
    return nftDetail
  } catch {
    return
  }
}

const convertCampaignToNFTDetail = (campaign: any) => ({
  campaignId: campaign.account.campaignId.toNumber(),
  mintAddress: campaign.publicKey.toBase58(),
  name: campaign.account.name,
  displayMintAddress: sliceString(campaign.publicKey.toBase58() || "", 4, 4),
  detail: null,
  metadata: {
    data: { uri: campaign.account.uri, name: campaign.account.name },
    mint: campaign.publicKey.toBase58(),
    collection: { key: "" },
  },
})

export const useNFTVoucher = () => {
  const trans = useTrans()
  const connection = useAtomValue(connectionAtom)
  const { wallet, address } = useWalletConnect()
  const network = useAtomValue(networkAtom)

  const provider = useMemo(
    () =>
      new anchor.AnchorProvider(connection, RANDOM_WALLET, {
        commitment: "confirmed",
      }),
    [connection],
  )

  const lendingProgramId = useMemo(
    () => (network === BlockchainNetwork.RENEC ? getRelendProgramId() : getSolanaProgramId()),
    [network],
  )

  const programId = useMemo(
    () =>
      network === BlockchainNetwork.RENEC
        ? Constants.VOUCHER_NFT_PROGRAM_ID
        : new PublicKey(SOLANA_NFT_VOUCHER_PROGRAM_ID),
    [network],
  )
  const tokenMetadataProgramId = useMemo(
    () =>
      network === BlockchainNetwork.RENEC ? Constants.TOKEN_METADATA_PROGRAM : Constants.SOLANA_TOKEN_METADATA_PROGRAM,
    [network],
  )

  const program = useMemo(
    () =>
      network === BlockchainNetwork.RENEC
        ? (new anchor.Program(
            VoucherNftIDL as anchor.Idl,
            programId,
            provider,
          ) as any as anchor.Program<RenecVoucherNftType>)
        : (new anchor.Program(
            VoucherNftIDL as anchor.Idl,
            programId,
            provider,
          ) as any as anchor.Program<SolanaVoucherNftType>),
    [network, programId, provider],
  )
  const nftPDA = useMemo(() => new NFTPDA(programId, tokenMetadataProgramId), [programId, tokenMetadataProgramId])

  const getConfigData = async () => {
    const { key: config } = nftPDA.config()
    try {
      return await program.account.config.fetch(config)
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const getCampaignData = async (campaignId: BN) => {
    const { key: campaign } = nftPDA.campaign(campaignId)
    try {
      return await program.account.campaign.fetch(campaign)
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const getEligibleUserData = useCallback(
    async (campaignId: BN) => {
      if (address) {
        const walletPubkey = new PublicKey(address)
        const { key: eligibleUsers } = nftPDA.eligibleUsers(walletPubkey, campaignId)
        try {
          const data = await program.account.eligibleUsers.fetch(eligibleUsers)
          return {
            isSucceeded: true,
            data,
          }
        } catch (error) {
          console.error(error)
          return {
            isSucceeded: false,
            error: stringifyVariable(error),
          }
        }
      }
    },
    [address, nftPDA, program.account.eligibleUsers],
  )

  const getAvailableNfts = useCallback(async () => {
    if (!address) return []

    const campaigns = await program.account.campaign.all()
    const availableCampaigns: NFTDetailType[] = []

    for (const campaign of campaigns) {
      if (!campaign.account.isActive) continue
      const { key: eligibleUser } = nftPDA.eligibleUsers(new PublicKey(address), campaign.account.campaignId)
      const isEligibleUserExist = (await connection.getAccountInfo(eligibleUser)) !== null
      const customCampaignScenario =
        "claimStrategy" in campaign.account
          ? campaign.account.claimStrategy
          : "customLogic" in campaign.account
            ? (campaign.account as any).customLogic
            : null
      if (customCampaignScenario === 0) {
        if (isEligibleUserExist) {
          availableCampaigns.push(convertCampaignToNFTDetail(campaign))
        }
      } else if (customCampaignScenario === 1) {
        const { key: obligationLending } = await nftPDA.obligationLending(
          new PublicKey(address),
          campaign.account.market.toBase58(),
          lendingProgramId,
        )
        const isObligationLendingExist = (await connection.getAccountInfo(obligationLending)) !== null
        if (!isObligationLendingExist && !isEligibleUserExist) {
          availableCampaigns.push(convertCampaignToNFTDetail(campaign))
        }
      } else if (customCampaignScenario === 2) {
        if (!isEligibleUserExist) {
          availableCampaigns.push(convertCampaignToNFTDetail(campaign))
        }
      }
    }

    const availablePromises = availableCampaigns.map(async (item, index) => {
      const value = await parseNFTDetail(item.metadata?.data.uri)
      availableCampaigns[index].detail = value ? value : null
      if (value && "image" in value) availableCampaigns[index].imageUrl = value ? String(value.image) : undefined
    })

    await Promise.all(availablePromises)

    return availableCampaigns.sort((a, b) => a.name.localeCompare(b.name)) // compare by name so we got the same order in the list
  }, [address, program.account.campaign, nftPDA, connection, lendingProgramId])

  const claimNFTVoucherWithCustomLogic = async (campaignId: BN) => {
    try {
      if (!wallet || !address) throw new Error("Wallet not found")

      const walletPubkey = new PublicKey(address)
      const mint = Keypair.generate()
      const userTokenAccount = await Token.getAssociatedTokenAddress(
        ASSOCIATED_TOKEN_PROGRAM_ID,
        TOKEN_PROGRAM_ID,
        mint.publicKey,
        walletPubkey,
        true,
      )

      const networkType = IS_MAINNET
        ? network === BlockchainNetwork.RENEC
          ? NetworkType.MainNet
          : NetworkType.SolanaMainnet
        : network === BlockchainNetwork.RENEC
          ? NetworkType.TestNet
          : NetworkType.SolanaDevnet

      const campaignData = await getCampaignData(campaignId)
      const { key: config } = nftPDA.config()
      const { key: vault } = nftPDA.vault(NFT_SEED)
      const { key: authority } = nftPDA.authority()
      const { key: campaign } = nftPDA.campaign(campaignId)
      const { collection: collectionMint } = await getConfigData()
      const { key: collectionMetadata } = await nftPDA.metadata(collectionMint)
      const { key: collectionMasterEdition } = await nftPDA.masterEdition(collectionMint)
      const { key: metadataAccount } = await nftPDA.metadata(mint.publicKey)
      const { key: masterEdition } = await nftPDA.masterEdition(mint.publicKey)
      const { key: eligibleUsers } = nftPDA.eligibleUsers(walletPubkey, campaignId)
      const { key: obligationLending } = await nftPDA.obligationLending(
        walletPubkey,
        Constants.ONCHAIN_ADDRESS[networkType].lendingMarketAddress.toBase58(),
        Constants.ONCHAIN_ADDRESS[networkType].lendingProgramId,
      )
      const { key: repayDebtVoucher } = nftPDA.repayDebtVoucher(mint.publicKey)
      const { key: borrowFeeVoucher } = nftPDA.borrowFeeVoucher(mint.publicKey)

      let remainingAccounts: { pubkey: PublicKey; isSigner: boolean; isWritable: boolean }[] = []
      const campaignNftScenario =
        "claimStrategy" in campaignData
          ? campaignData.claimStrategy
          : "customLogic" in campaignData
            ? (campaignData as any).customLogic
            : null
      const campaignVoucherInfo =
        "voucherInfo" in campaignData
          ? campaignData.voucherInfo
          : "voucherType" in campaignData
            ? campaignData.voucherType
            : null
      if (campaignNftScenario === 0) {
        remainingAccounts = [
          {
            // operator
            pubkey: campaignData.operator,
            isSigner: false,
            isWritable: true,
          },
          {
            // eligibleUsers
            pubkey: eligibleUsers,
            isSigner: false,
            isWritable: true,
          },
        ]
      } else if (campaignNftScenario === 1) {
        remainingAccounts = [
          {
            // obligationLending
            pubkey: obligationLending,
            isSigner: false,
            isWritable: false,
          },
          {
            // eligibleUsers
            pubkey: eligibleUsers,
            isSigner: false,
            isWritable: true,
          },
        ]
      } else if (campaignNftScenario === 2) {
        remainingAccounts = [
          {
            // eligibleUsers
            pubkey: eligibleUsers,
            isSigner: false,
            isWritable: true,
          },
        ]
      }

      const userClaimNftIns = await program.methods
        .userClaimNftWithCustomLogic()
        .accounts({
          config,
          vault,
          user: walletPubkey,
          authority,
          campaign,
          mint: mint.publicKey,
          userTokenAccount,
          metadataAccount,
          masterEdition,
          collectionMint,
          collectionMetadataAccount: collectionMetadata,
          collectionMasterEdition,
          voucherInfomation: campaignVoucherInfo === 0 ? repayDebtVoucher : borrowFeeVoucher,
          tokenMetadataProgram: tokenMetadataProgramId,
        })
        .remainingAccounts(remainingAccounts)
        .instruction()

      const modifyUnitIns = modifyComputeUnitIx()
      const { blockhash } = await connection.getLatestBlockhash()
      const transaction = new Transaction().add(modifyUnitIns, userClaimNftIns)
      transaction.recentBlockhash = blockhash
      transaction.feePayer = walletPubkey
      const messageV0 = new TransactionMessage({
        payerKey: new PublicKey(address),
        recentBlockhash: blockhash,
        instructions: transaction.instructions,
      }).compileToV0Message()
      const versionedTransaction = new VersionedTransaction(messageV0)
      const result = await connection.simulateTransaction(versionedTransaction)
      if (result.value.err) {
        const logs = result.value.logs ? result.value.logs : []
        const logToThrow = logs.length > 0 ? logs : [stringifyVariable(result.value.err)]
        throw new SendTransactionError("failed to send transaction: " + logToThrow, logs)
      }

      let txSignature: string
      if (wallet.adapter instanceof DemonWalletAdapter) {
        txSignature = await (wallet.adapter as DemonWalletAdapter).sendTransactionWithSigners(transaction, connection, [
          mint,
        ])
      } else {
        txSignature = await wallet.adapter.sendTransaction(transaction, connection, {
          signers: [mint],
        })
      }
      await waitForTransactionConfirmation(network, txSignature)

      return {
        isSucceeded: true,
        tx: txSignature,
      }
    } catch (err) {
      console.error(err)
      return commonErrorResponse(err, trans)
    }
  }
  return { claimNFTVoucherWithCustomLogic, getEligibleUserData, getAvailableNfts }
}
